import authService from './auth.service';
import {
  parseSelfQrErrorResponse,
  networkError,
  SelfQrApiError,
} from '../utils/selfQrErrorParser';
import type {
  SelfQrInstance,
  SelfQrCreateInstanceIn,
  SelfQrUpdateInstanceIn,
  SelfQrSessionOut,
  SelfQrQrOut,
  SelfQrChatOut,
  SelfQrChatStatus,
  SelfQrMessageOut,
  SelfQrSendTextIn,
  SelfQrSendMediaIn,
  SelfQrSendResponse,
  SelfQrMarkChatReadResponse,
  SelfQrReactToMessageResponse,
  SelfQrResyncChatResponse,
  SelfQrStartChatIn,
  SelfQrStartChatResponse,
  SelfQrCheckPhoneResponse,
  SelfQrUpdateChatIn,
  SelfQrDeleteInstanceResponse,
  // Extended send payloads (backend PR #298)
  SelfQrSendVoiceIn,
  SelfQrSendVideoIn,
  SelfQrSendLocationIn,
  SelfQrSendContactIn,
  SelfQrSendPollIn,
  SelfQrSendListIn,
  SelfQrSendButtonsIn,
  SelfQrSendLinkPreviewIn,
  // Message actions
  SelfQrForwardMessageIn,
  SelfQrEditMessageIn,
  SelfQrEditMessageResponse,
  SelfQrStarIn,
  SelfQrPinIn,
  // Session config
  SelfQrSessionConfigIn,
  // Response envelopes (backend PR #298)
  OutboundResult,
  OkChatToggle,
  OkMessageToggle,
  SelfQrChatPicture,
  SelfQrChatsOverview,
  SelfQrSessionsList,
  SelfQrSessionMe,
  SelfQrSessionConfigResponse,
  SelfQrServerInfo,
} from '../types/self-qr.types';

class SelfQrService {
  private readonly BASE = '/api/v1/integrations/providers/self-qr';

  // ─────────────── Instances ───────────────

  async listInstances(): Promise<SelfQrInstance[]> {
    return this.request<SelfQrInstance[]>(`${this.BASE}/instances`);
  }

  async createInstance(body: SelfQrCreateInstanceIn): Promise<SelfQrInstance> {
    return this.request<SelfQrInstance>(`${this.BASE}/instances`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  async updateInstance(
    instanceId: number,
    body: SelfQrUpdateInstanceIn,
  ): Promise<SelfQrInstance> {
    return this.request<SelfQrInstance>(
      `${this.BASE}/instances/${instanceId}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
  }

  // Полное удаление инстанса: backend best-effort отвязывает WhatsApp-сессии
  // в WAHA и чистит чаты/сессии/entity-links в БД.
  async deleteInstance(instanceId: number): Promise<SelfQrDeleteInstanceResponse> {
    return this.request<SelfQrDeleteInstanceResponse>(
      `${this.BASE}/instances/${instanceId}`,
      { method: 'DELETE' },
    );
  }

  // ─────────────── Sessions ───────────────

  async startSession(instanceId: number): Promise<SelfQrSessionOut> {
    return this.request<SelfQrSessionOut>(
      `${this.BASE}/instances/${instanceId}/sessions`,
      { method: 'POST' },
    );
  }

  async getSession(sessionId: number): Promise<SelfQrSessionOut> {
    return this.request<SelfQrSessionOut>(`${this.BASE}/sessions/${sessionId}`);
  }

  async getQr(sessionId: number): Promise<SelfQrQrOut> {
    return this.request<SelfQrQrOut>(`${this.BASE}/sessions/${sessionId}/qr`);
  }

  async disconnectSession(sessionId: number): Promise<SelfQrSessionOut> {
    return this.request<SelfQrSessionOut>(
      `${this.BASE}/sessions/${sessionId}`,
      { method: 'DELETE' },
    );
  }

  // ─────────────── Messages ───────────────

  async sendText(
    sessionId: number,
    body: SelfQrSendTextIn,
  ): Promise<SelfQrSendResponse> {
    return this.request<SelfQrSendResponse>(
      `${this.BASE}/sessions/${sessionId}/messages`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
  }

  async sendMedia(
    sessionId: number,
    body: SelfQrSendMediaIn,
  ): Promise<SelfQrSendResponse> {
    return this.request<SelfQrSendResponse>(
      `${this.BASE}/sessions/${sessionId}/messages/media`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
  }

  // ─────────────── Chats ───────────────

  async listChats(
    instanceId: number,
    opts?: {
      // Один статус или CSV ("new,active,unanswered,closed") — бэк поддерживает CSV
      status_filter?: SelfQrChatStatus | string;
      limit?: number;
      offset?: number;
      // Серверный фильтр по архиву (Finding 1A).
      archived?: boolean;
    },
  ): Promise<SelfQrChatOut[]> {
    const q = new URLSearchParams();
    if (opts?.status_filter) q.set('status_filter', opts.status_filter);
    if (typeof opts?.limit === 'number') q.set('limit', String(opts.limit));
    if (typeof opts?.offset === 'number') q.set('offset', String(opts.offset));
    // COORDINATION: имя параметра архива ('archived') сверить с backend; альтернатива — status_filter
    if (typeof opts?.archived === 'boolean') q.set('archived', String(opts.archived));
    const suffix = q.toString();
    return this.request<SelfQrChatOut[]>(
      `${this.BASE}/instances/${instanceId}/chats${suffix ? `?${suffix}` : ''}`,
    );
  }

  async listMessages(
    chatId: number,
    opts?: { limit?: number; offset?: number },
  ): Promise<SelfQrMessageOut[]> {
    const q = new URLSearchParams();
    if (typeof opts?.limit === 'number') q.set('limit', String(opts.limit));
    if (typeof opts?.offset === 'number') q.set('offset', String(opts.offset));
    const suffix = q.toString();
    return this.request<SelfQrMessageOut[]>(
      `${this.BASE}/chats/${chatId}/messages${suffix ? `?${suffix}` : ''}`,
    );
  }

  async updateChat(
    chatId: number,
    body: SelfQrUpdateChatIn,
  ): Promise<SelfQrChatOut> {
    return this.request<SelfQrChatOut>(
      `${this.BASE}/chats/${chatId}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
  }

  async checkPhone(
    instanceId: number,
    phone: string,
  ): Promise<SelfQrCheckPhoneResponse> {
    const q = new URLSearchParams({ phone });
    return this.request<SelfQrCheckPhoneResponse>(
      `${this.BASE}/instances/${instanceId}/check-phone?${q.toString()}`,
    );
  }

  async startChat(
    instanceId: number,
    body: SelfQrStartChatIn,
  ): Promise<SelfQrStartChatResponse> {
    return this.request<SelfQrStartChatResponse>(
      `${this.BASE}/instances/${instanceId}/start-chat`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
  }

  async resyncChat(
    chatId: number,
    opts?: { limit?: number },
  ): Promise<SelfQrResyncChatResponse> {
    const q = new URLSearchParams();
    if (typeof opts?.limit === 'number') q.set('limit', String(opts.limit));
    const suffix = q.toString();
    return this.request<SelfQrResyncChatResponse>(
      `${this.BASE}/chats/${chatId}/resync${suffix ? `?${suffix}` : ''}`,
      { method: 'POST' },
    );
  }

  async reactToMessage(
    messageId: number,
    emoji: string | null,
  ): Promise<SelfQrReactToMessageResponse> {
    return this.request<SelfQrReactToMessageResponse>(
      `${this.BASE}/messages/${messageId}/react`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emoji: emoji ?? '' }),
      },
    );
  }

  async markChatRead(
    chatId: number,
    opts?: { sendSeen?: boolean },
  ): Promise<SelfQrMarkChatReadResponse> {
    const q = new URLSearchParams();
    if (opts?.sendSeen === false) q.set('send_seen', 'false');
    const suffix = q.toString();
    return this.request<SelfQrMarkChatReadResponse>(
      `${this.BASE}/chats/${chatId}/read${suffix ? `?${suffix}` : ''}`,
      { method: 'POST' },
    );
  }

  // ─────────────── Extended send (backend PR #298) ───────────────
  // {sessionId} — числовой PK сессии. body.chat_id — строка JID/номер.

  async sendVoice(
    sessionId: number,
    body: SelfQrSendVoiceIn,
  ): Promise<OutboundResult> {
    return this.request<OutboundResult>(
      `${this.BASE}/sessions/${sessionId}/messages/voice`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
  }

  async sendVideo(
    sessionId: number,
    body: SelfQrSendVideoIn,
  ): Promise<OutboundResult> {
    return this.request<OutboundResult>(
      `${this.BASE}/sessions/${sessionId}/messages/video`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
  }

  async sendLocation(
    sessionId: number,
    body: SelfQrSendLocationIn,
  ): Promise<OutboundResult> {
    return this.request<OutboundResult>(
      `${this.BASE}/sessions/${sessionId}/messages/location`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
  }

  async sendContact(
    sessionId: number,
    body: SelfQrSendContactIn,
  ): Promise<OutboundResult> {
    return this.request<OutboundResult>(
      `${this.BASE}/sessions/${sessionId}/messages/contact`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
  }

  async sendPoll(
    sessionId: number,
    body: SelfQrSendPollIn,
  ): Promise<OutboundResult> {
    return this.request<OutboundResult>(
      `${this.BASE}/sessions/${sessionId}/messages/poll`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
  }

  async sendList(
    sessionId: number,
    body: SelfQrSendListIn,
  ): Promise<OutboundResult> {
    return this.request<OutboundResult>(
      `${this.BASE}/sessions/${sessionId}/messages/list`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
  }

  async sendButtons(
    sessionId: number,
    body: SelfQrSendButtonsIn,
  ): Promise<OutboundResult> {
    return this.request<OutboundResult>(
      `${this.BASE}/sessions/${sessionId}/messages/buttons`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
  }

  async sendLinkPreview(
    sessionId: number,
    body: SelfQrSendLinkPreviewIn,
  ): Promise<OutboundResult> {
    return this.request<OutboundResult>(
      `${this.BASE}/sessions/${sessionId}/messages/link-preview`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
  }

  // ─────────────── Typing (backend PR #298) ───────────────
  // {chatId} — локальный числовой PK чата.

  async startTyping(chatId: number): Promise<OkChatToggle> {
    return this.request<OkChatToggle>(
      `${this.BASE}/chats/${chatId}/typing/start`,
      { method: 'POST' },
    );
  }

  async stopTyping(chatId: number): Promise<OkChatToggle> {
    return this.request<OkChatToggle>(
      `${this.BASE}/chats/${chatId}/typing/stop`,
      { method: 'POST' },
    );
  }

  // ─────────────── Message actions (backend PR #298) ───────────────
  // {messageId} — локальный числовой PK сообщения.

  async forwardMessage(
    messageId: number,
    body: SelfQrForwardMessageIn,
  ): Promise<OutboundResult> {
    return this.request<OutboundResult>(
      `${this.BASE}/messages/${messageId}/forward`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
  }

  async editMessage(
    messageId: number,
    body: SelfQrEditMessageIn,
  ): Promise<SelfQrEditMessageResponse> {
    return this.request<SelfQrEditMessageResponse>(
      `${this.BASE}/messages/${messageId}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
  }

  async deleteMessage(messageId: number): Promise<OkMessageToggle> {
    return this.request<OkMessageToggle>(
      `${this.BASE}/messages/${messageId}`,
      { method: 'DELETE' },
    );
  }

  async starMessage(
    messageId: number,
    body: SelfQrStarIn,
  ): Promise<OkMessageToggle> {
    return this.request<OkMessageToggle>(
      `${this.BASE}/messages/${messageId}/star`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
  }

  async pinMessage(
    messageId: number,
    body: SelfQrPinIn,
  ): Promise<OkMessageToggle> {
    return this.request<OkMessageToggle>(
      `${this.BASE}/messages/${messageId}/pin`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
  }

  // ─────────────── Chat management (backend PR #298) ───────────────
  // {chatId} — локальный PK чата. {instanceId} — PK канала.

  async getChatsOverview(
    instanceId: number,
    opts?: { limit?: number; offset?: number },
  ): Promise<SelfQrChatsOverview> {
    const q = new URLSearchParams();
    if (typeof opts?.limit === 'number') q.set('limit', String(opts.limit));
    if (typeof opts?.offset === 'number') q.set('offset', String(opts.offset));
    const suffix = q.toString();
    return this.request<SelfQrChatsOverview>(
      `${this.BASE}/instances/${instanceId}/chats/overview${suffix ? `?${suffix}` : ''}`,
    );
  }

  async getChatPicture(chatId: number): Promise<SelfQrChatPicture> {
    return this.request<SelfQrChatPicture>(
      `${this.BASE}/chats/${chatId}/picture`,
    );
  }

  async archiveChat(chatId: number): Promise<SelfQrChatOut> {
    return this.request<SelfQrChatOut>(
      `${this.BASE}/chats/${chatId}/archive`,
      { method: 'POST' },
    );
  }

  async unarchiveChat(chatId: number): Promise<SelfQrChatOut> {
    return this.request<SelfQrChatOut>(
      `${this.BASE}/chats/${chatId}/unarchive`,
      { method: 'POST' },
    );
  }

  async markChatUnread(chatId: number): Promise<OkChatToggle> {
    return this.request<OkChatToggle>(
      `${this.BASE}/chats/${chatId}/unread`,
      { method: 'POST' },
    );
  }

  async blockChat(chatId: number): Promise<SelfQrChatOut> {
    return this.request<SelfQrChatOut>(
      `${this.BASE}/chats/${chatId}/block`,
      { method: 'POST' },
    );
  }

  async unblockChat(chatId: number): Promise<SelfQrChatOut> {
    return this.request<SelfQrChatOut>(
      `${this.BASE}/chats/${chatId}/unblock`,
      { method: 'POST' },
    );
  }

  async deleteChatRemote(chatId: number): Promise<OkChatToggle> {
    return this.request<OkChatToggle>(
      `${this.BASE}/chats/${chatId}/remote`,
      { method: 'DELETE' },
    );
  }

  // ─────────────── Sessions / Observability (backend PR #298) ───────────────

  async listSessions(): Promise<SelfQrSessionsList> {
    return this.request<SelfQrSessionsList>(`${this.BASE}/sessions`);
  }

  async getSessionMe(sessionId: number): Promise<SelfQrSessionMe> {
    return this.request<SelfQrSessionMe>(
      `${this.BASE}/sessions/${sessionId}/me`,
    );
  }

  async updateSessionConfig(
    sessionId: number,
    body: SelfQrSessionConfigIn,
  ): Promise<SelfQrSessionConfigResponse> {
    return this.request<SelfQrSessionConfigResponse>(
      `${this.BASE}/sessions/${sessionId}/config`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
  }

  async serverPing(): Promise<SelfQrServerInfo> {
    return this.request<SelfQrServerInfo>(`${this.BASE}/server/ping`);
  }

  async serverHealth(): Promise<SelfQrServerInfo> {
    return this.request<SelfQrServerInfo>(`${this.BASE}/server/health`);
  }

  async serverVersion(): Promise<SelfQrServerInfo> {
    return this.request<SelfQrServerInfo>(`${this.BASE}/server/version`);
  }

  async serverStatus(): Promise<SelfQrServerInfo> {
    return this.request<SelfQrServerInfo>(`${this.BASE}/server/status`);
  }

  async serverEnvironment(): Promise<SelfQrServerInfo> {
    return this.request<SelfQrServerInfo>(`${this.BASE}/server/environment`);
  }

  // ─────────────── private ───────────────

  private async request<T>(url: string, init?: RequestInit): Promise<T> {
    let res: Response;
    try {
      res = await authService.fetchWithAuth(url, init);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      throw new SelfQrApiError(networkError(msg));
    }

    if (!res.ok) {
      const parsed = await parseSelfQrErrorResponse(res);
      throw new SelfQrApiError(parsed);
    }

    // DELETE может вернуть 204 без тела
    if (res.status === 204) {
      return undefined as unknown as T;
    }
    return (await res.json()) as T;
  }
}

const selfQrService = new SelfQrService();
export default selfQrService;
